"use client";

import { useCallback, useMemo } from "react";
import { TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { Subcategory } from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/types";
import { ElementType } from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementClicked } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementClicked";
import { cn } from "@/lib/utils";
import { sanitizeTabValue } from "@/utils/sanitizeTabValue";
import { hyphenateForTagular } from "@/utils/tagular/helpers";

type SubcategoryTabBarProps = {
  subcategories?: Subcategory[] | null;
  categoryLabel: string;
  children: (subcategory: Subcategory) => React.ReactNode;
};

export function SubcategoryTabBar({
  subcategories,
  categoryLabel,
  children,
}: SubcategoryTabBarProps) {
  const subs = subcategories ?? [];
  const categoryLabelDashed = hyphenateForTagular(categoryLabel);

  const webElement: ElementClicked["webElement"] = useMemo(
    () => ({
      elementType: ElementType.Button,
      htmlId: "subcategory-buttons",
      location: "trending-products",
      position: "top",
      name: "trending-products-lob",
      text: "trending products lob",
    }),
    [],
  );

  const { elementClicked } = useElementEvents({ webElement });

  const clickSubcategoryTab = useCallback(
    (
      webElementOverrides: Partial<NonNullable<ElementClicked["webElement"]>>,
    ) => {
      elementClicked({ webElement: webElementOverrides });
    },
    [elementClicked],
  );

  return (
    <>
      <TabsList className="overflow-x-auto justify-start h-auto py-5 gap-2 pl-2">
        {subs.map((subcategory) => {
          const sanitizedSubcategoryValue = sanitizeTabValue(
            subcategory.subcategoryLabel,
          );
          if (subcategory.subcategoryLabel.trim() === "") {
            return null;
          }

          const subcategoryLabelDashed = hyphenateForTagular(
            subcategory.subcategoryLabel,
          );

          return (
            <TabsTrigger
              key={sanitizedSubcategoryValue}
              value={sanitizedSubcategoryValue}
              onClick={() => {
                clickSubcategoryTab({
                  name: `${categoryLabelDashed}-${subcategoryLabelDashed}`,
                  text: `${categoryLabel}/${subcategory.subcategoryLabel}`,
                });
              }}
              className={cn(
                "grow-0 rounded-[0.25rem] no-underline py-1 px-2",
                "text-primary text-sm font-bold bg-putty-400",
                "hover:bg-primary hover:text-white",
                "data-[state=active]:bg-primary-500",
                "data-[state=active]:text-white data-[state=active]:hover:text-white",
                "first:rounded-[0.25rem] last:rounded-[0.25rem]",
              )}
            >
              {subcategory.subcategoryLabel}
            </TabsTrigger>
          );
        })}
      </TabsList>

      {subs.map((subcategory) => {
        if (subcategory.subcategoryLabel.trim() === "") {
          return null;
        }
        const sanitizedSubcategoryValue = sanitizeTabValue(
          subcategory.subcategoryLabel,
        );
        return (
          <TabsContent
            key={`${categoryLabel}-${sanitizedSubcategoryValue}`}
            value={sanitizedSubcategoryValue}
            className={cn("mt-0")}
          >
            {children(subcategory)}
          </TabsContent>
        );
      })}
    </>
  );
}
